// Systems.tsx
import React, { useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AccordionPanel from "../components/system/AccordionPanel";
import ConfigDetails from "../components/system/ConfigDetails";
import SystemDetails from "../components/system/SystemDetails";
import SystemSettingsForm from "../components/system/forms/SystemSettingsForm";
import { SystemsProvider, useSystemsContext } from "../context/SystemsContext";
import SystemActionButtons from "../components/system/SystemActionButtons";
import { useSession } from "../context/SessionContext";
import SystemJsonEditor from "../components/system/SystemJsonEditor";
import ServiceDefinitionsManager from "../components/system/ServiceDefinitionsManager";

// This new component decides which main view to show
const MainContentPanel: React.FC = () => {
  const { isLoadingSystem, selectedSection, selectedConfig: expandedConfigId, configs } = useSystemsContext();
  const { user } = useSession();
  const theme = useTheme();
  const accent = theme.palette.status.accent;
  const isDark = theme.palette.mode === "dark";
  const [showJsonEditor, setShowJsonEditor] = useState(false);
  const selectedConfig = configs.find((c: any) => c.clientProcess?.name === expandedConfigId);
  const configSectionData = selectedConfig?.[selectedSection || ""];
  const isAdmin = user?.role === "Admin";
  const canUseJsonEditor =
    isAdmin &&
    selectedSection !== "createSystem" &&
    selectedSection !== "serviceDefinitions";

  if (isLoadingSystem) {
    return (
      <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={60} />
      </Box>
    );
  }
  if (selectedSection === "createSystem") {
    return <SystemSettingsForm />;
  }
  if (selectedSection === "serviceDefinitions") {
    if (!isAdmin) {
      return (
        <Typography variant="body1" sx={{ mt: 4 }}>
          You do not have permission to view Service Definitions.
        </Typography>
      );
    }
    return <ServiceDefinitionsManager />;
  }

  if (showJsonEditor && canUseJsonEditor) {
    return (
      <Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <ToggleButtonGroup
            exclusive
            value={showJsonEditor ? "json" : "form"}
            onChange={(_, mode) => {
              if (!mode) return;
              setShowJsonEditor(mode === "json");
            }}
            sx={{
              "& .MuiToggleButton-root": {
                borderColor: alpha(accent, isDark ? 0.75 : 0.85),
                color: accent,
                fontWeight: 600,
                px: 2,
                py: 0.5,
                "&:hover": {
                  borderColor: accent,
                  backgroundColor: alpha(accent, 0.1),
                },
                "&.Mui-selected": {
                  backgroundColor: accent,
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: accent,
                    opacity: 0.92,
                  },
                },
              },
            }}
          >
            <ToggleButton value="form">FORM</ToggleButton>
            <ToggleButton value="json">JSON</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <SystemJsonEditor />
      </Box>
    );
  }

  if (canUseJsonEditor) {
    return (
      <Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <ToggleButtonGroup
            exclusive
            value={showJsonEditor ? "json" : "form"}
            onChange={(_, mode) => {
              if (!mode) return;
              setShowJsonEditor(mode === "json");
            }}
            sx={{
              "& .MuiToggleButton-root": {
                borderColor: alpha(accent, isDark ? 0.75 : 0.85),
                color: accent,
                fontWeight: 600,
                px: 2,
                py: 0.5,
                "&:hover": {
                  borderColor: accent,
                  backgroundColor: alpha(accent, 0.1),
                },
                "&.Mui-selected": {
                  backgroundColor: accent,
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: accent,
                    opacity: 0.92,
                  },
                },
              },
            }}
          >
            <ToggleButton value="form">FORM</ToggleButton>
            <ToggleButton value="json">JSON</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {selectedSection === "systemInfo" ? (
          <SystemDetails />
        ) : configSectionData ? (
          <ConfigDetails />
        ) : (
          <Typography variant="body1" sx={{ mt: 4 }}>
            Select a section to view details
          </Typography>
        )}
      </Box>
    );
  }

  if (selectedSection === "systemInfo") {
    return <SystemDetails />;
  }
  if (configSectionData) {
    return <ConfigDetails />;
  }
  return <Typography variant="body1" sx={{ mt: 4 }}>Select a section to view details</Typography>;
};

const Systems: React.FC = () => {
  return (
    <SystemsProvider>
      <Box sx={{ display: 'flex', height: '100%', minHeight: 0, overflow: 'hidden' }}>
        {/* AccordionPanel will now use the context internally, no props needed! */}
        <AccordionPanel />

        {/* The right panel is now clean */}
        <Box sx={{ flex: 1, p: 3, overflow: "auto" }}>
          <MainContentPanel />
          <SystemActionButtons />
        </Box>
      </Box>

    </SystemsProvider>
  );
};



export default Systems;
